import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useUser } from '../contexts/UserContext';
import { useSchool } from '../contexts/SchoolContext';
import { toast } from 'react-hot-toast';
import languageService from '../utils/languageService';
import { getPublicSelectedLanguages } from '../utils/api';
import Navbar from '../components/Navbar';
import LandingNavbar from '../components/LandingNavbar';

// Get API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

const LoginPage = ({ isMainDomain }) => {
  const { t, i18n } = useTranslation();
  const { updateUser } = useUser();
  const { school, isSubdomainRoute } = useSchool();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user was redirected from successful registration
  const registrationSuccess = location.state?.registrationSuccess;
  const verificationRequired = location.state?.verificationRequired;
  const registeredEmail = location.state?.email;
  const schoolName = location.state?.schoolName;
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languageChecked, setLanguageChecked] = useState(false);
  
  // Choose the correct Navbar based on context
  const AppropriateNavbar = isMainDomain ? LandingNavbar : Navbar;
  
  // Check if user's preferred language is supported by this school
  useEffect(() => {
    const checkLanguageSupport = async () => {
      if (school?.id && isSubdomainRoute && !languageChecked) {
        try {
          // Get user's preferred language
          const userPreferredLanguage = languageService.getPreferredLanguage();
          console.log('[LoginPage] User preferred language:', userPreferredLanguage);
          
          // Get supported languages for this school
          const selectedLanguages = await getPublicSelectedLanguages(school.id);
          console.log('[LoginPage] School supported languages:', selectedLanguages);
          
          // Check if user's preferred language is supported
          const isSupported = selectedLanguages.some(
            lang => lang.code === userPreferredLanguage
          );
          
          if (!isSupported) {
            console.log('[LoginPage] User language not supported, switching to school default');
            // Get the school's default language
            if (selectedLanguages.length > 0) {
              const defaultLang = selectedLanguages[0]; // First language is default
              console.log('[LoginPage] Switching to school default language:', defaultLang.code);
              await languageService.temporarilySwitchLanguage(defaultLang.code, dispatch);
            }
          } else {
            console.log('[LoginPage] User language is supported by school');
          }
          
          setLanguageChecked(true);
        } catch (err) {
          console.error('[LoginPage] Error checking language support:', err);
        }
      }
    };
    
    checkLanguageSupport();
  }, [school, isSubdomainRoute, dispatch, languageChecked]);
  
  // Display success toast if redirected from registration
  useEffect(() => {
    if (registrationSuccess) {
      if (verificationRequired) {
        toast.success(t('registrationVerify', 'Registration successful! Please check your email to verify your account.'));
      } else {
        toast.success(t('registrationSuccess', 'Registration successful! You can now log in.'));
      }
    }
  }, [registrationSuccess, verificationRequired, t]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username, 
          password: encodeURIComponent(password),
          // Include subdomain if on a subdomain route
          subdomain: school?.subdomain || undefined 
        }),
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Login failed');
      }
      
      const { accessToken, refreshToken, user } = await res.json();
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      // Update the user context
      updateUser(user);
      
      // Apply the user's language preference if available
      if (user.language_preference) {
        await languageService.changeLanguage(user.language_preference, dispatch, true);
      } else {
        // Load language preference from the server or use cookie/default
        await languageService.loadPreferredLanguage(dispatch, true);
      }
      
      toast.success(t('loginSuccess', 'Login successful!'));
      
      // Redirect based on login context
      if (isMainDomain) {
        // If logging in from the main domain, go to the school selector dashboard
        navigate('/dashboard');
      } else {
        // If logging in from a subdomain, redirect based on role
        if (school && school.subdomain && user) {
          // Determine the user's highest role for this specific school
          const schoolRoles = user.schools?.find(s => s.subdomain === school.subdomain)?.roles || [];
          let highestRole = 'None';
          if (schoolRoles.includes('Superadmin')) highestRole = 'Superadmin';
          else if (schoolRoles.includes('Admin')) highestRole = 'Admin';
          else if (schoolRoles.includes('Editor')) highestRole = 'Editor';
          else if (schoolRoles.includes('Viewer')) highestRole = 'Viewer';

          if (highestRole === 'Viewer') {
            // Redirect Viewers to the preview page
            console.log('[LoginRedirect] User role is Viewer, redirecting to preview');
            navigate(`/${school.subdomain}/admin/preview`);
          } else if (highestRole !== 'None') {
            // Redirect Editor/Admin/Superadmin to the main admin dashboard
            console.log(`[LoginRedirect] User role is ${highestRole}, redirecting to admin dashboard`);
            navigate(`/${school.subdomain}/admin`);
          } else {
             // If user has NO roles for this school (shouldn't happen if login succeeded), go to main dashboard
             console.warn('[LoginRedirect] User has no roles for this school, redirecting to main dashboard');
             navigate('/dashboard');
          }
        } else {
          // Fallback if school or user info is not available (shouldn't typically happen)
          console.warn('[LoginRedirect] School or user info missing, falling back to main dashboard');
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError(err.message || t('loginFailed', 'Login failed. Please check your credentials.'));
      toast.error(err.message || t('loginFailed', 'Login failed'));
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <AppropriateNavbar />
      
      <div className="max-w-md mx-auto pt-16 px-4">
        <div className="bg-white p-8 border rounded-lg shadow-md">
          {registrationSuccess && !verificationRequired && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <p className="font-medium">{t('registrationSuccessTitle', 'Account Created!')}</p>
              <p>{t('registrationSuccessMessage', schoolName 
                ? `Your account for ${schoolName} has been created successfully. Please log in to access your dashboard.`
                : 'Your account has been created successfully. Please log in to continue.')}</p>
            </div>
          )}
          
          {registrationSuccess && verificationRequired && (
            <div className="mb-6 p-4 bg-blue-100 border border-blue-400 text-blue-700 rounded-md">
              <p className="font-medium">{t('verificationSentTitle', 'Verification Email Sent')}</p>
              <p>{t('verificationSentMessage', 
                `We've sent a verification email to ${registeredEmail}. Please check your inbox and click the verification link to activate your account.`)}</p>
            </div>
          )}
          
          {school && (
            <div className="mb-6 text-center">
              {school.logo_url && (
                <img 
                  src={school.logo_url} 
                  alt={school.name} 
                  className="h-16 mx-auto mb-4"
                />
              )}
              <h1 className="text-xl font-semibold">{school.name}</h1>
            </div>
          )}
          
          <h2 className="text-2xl font-bold mb-6 text-center">
            {school 
              ? t('loginToSchool', 'Login to {{schoolName}}', { schoolName: school.name })
              : isMainDomain 
                ? t('loginToAccount', 'Login to Your Account')
                : t('login', 'Login')}
          </h2>
          
          {isMainDomain && (
            <p className="mb-6 text-center text-gray-600">
              {t('mainDomainLoginDescription', 'Log in to access all your schools')}
            </p>
          )}
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
                {t('usernameOrEmail', 'Username or Email')}
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={isSubmitting}
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
                {t('password', 'Password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full p-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={isSubmitting}
              />
            </div>
            
            {error && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded focus:outline-none transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('loggingIn', 'Logging in...') : t('login', 'Login')}
            </button>
            
            {!isSubdomainRoute && (
              <div className="mt-4 text-center text-gray-600">
                <p>
                  {t('noAccount', "Don't have an account?")}{' '}
                  <Link to="/signup" className="text-blue-600 hover:underline">
                    {t('createAccount', 'Create an account')}
                  </Link>
                </p>
                <p className="mt-2">
                  <Link to="/forgot-password" className="text-blue-600 hover:underline">
                    {t('loginPage.forgotPassword', 'Forgot your password?')}
                  </Link>
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 